import React from "react";
import KpiCard from "./KpiCard";
import HelpTooltip from "../common/HelpTooltip";
import { FORFETTARIO_THRESHOLD, FORFETTARIO_THRESHOLD_EXIT } from "../../constants/ateco";

// Formattazione italiana robusta: 1.234,56 €
function formatCurrency(v, currency = "EUR") {
  const value = Number(v || 0);
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  // Formatta con 2 decimali, poi gestisci separatori
  const [intPart, decPart] = abs.toFixed(2).split(".");

  // Aggiungi separatore migliaia (.)
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return `${sign}${groupedInt},${decPart} €`;
}

export default function KpiRow({ kpis, currency = "EUR", darkMode = false }) {
  const trend =
    kpis.revenueDeltaPct > 0 ? "up" : kpis.revenueDeltaPct < 0 ? "down" : "flat";

  const incassiDeltaPct = kpis.incassiDeltaPct || 0;
  const incassiTrend = incassiDeltaPct > 0 ? "up" : incassiDeltaPct < 0 ? "down" : "flat";

  // Tasse: se per l'anno risultano versamenti, il numero principale è il RESIDUO
  // (dovuto − versato), con il dettaglio sotto. Senza versamenti i due numeri
  // coincidono, quindi si mostra solo il dovuto come prima.
  // Terminologia allineata a FiscalSummaryCard: Dovuto / Versato / Residuo.
  const versatoTasse = kpis.versatoTotale || 0;
  const haVersamenti = versatoTasse > 0;
  const tasseValue = haVersamenti ? (kpis.residuoAnnual || 0) : kpis.setAsideAnnual;
  const tasseMonthly = haVersamenti ? (kpis.residuoMonthly || 0) : kpis.setAsideMonthly;
  const tasseTooltip = haVersamenti
    ? `Quanto ti resta da mettere da parte per il ${kpis.selectedYear}, al netto di quanto hai già versato. Il dovuto lordo e il versato sono nel dettaglio qui sotto.`
    : "Stima di imposta sostitutiva + contributi INPS calcolati sugli incassi. Non hai ancora registrato versamenti per questo anno.";

  // Soglia basata su INCASSI (non fatturato emesso)
  // Warning: giallo >= 70k, rosso >= 80k, superato > 85k (uscita) / 100k (uscita immediata)
  const percentualeSoglia = kpis.percentualeSoglia || 0;
  const isNearThreshold = kpis.isNearThreshold || false;      // >= 70k
  const isDangerThreshold = kpis.isDangerThreshold || false;  // >= 80k
  const isOverThreshold = kpis.isOverThreshold || false;      // > 85k
  // Soglie effettive: ragguagliate ai giorni di attività nel primo anno, altrimenti piene.
  const soglia = kpis.soglia || FORFETTARIO_THRESHOLD;
  const sogliaExit = kpis.sogliaExit || FORFETTARIO_THRESHOLD_EXIT;
  const isOverExitThreshold = (kpis.incassiAnno || 0) > sogliaExit; // oltre uscita immediata
  // Percentuale calcolata sulla soglia estesa per la barra a doppio traguardo
  const percentualeSogliaEstesa = Math.min(100, ((kpis.incassiAnno || 0) / sogliaExit) * 100);
  const marcatore85kPct = (soglia / sogliaExit) * 100;

  return (
    <div className="kpi-section space-y-4">
      {/* Prima riga: KPI principali */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Fatturato emesso"
          value={formatCurrency(kpis.revenueYTD, currency)}
          footerLeft={`${kpis.revenueDeltaPct >= 0 ? "+" : ""}${kpis.revenueDeltaPct.toFixed(2)}%`}
          footerRight={`${kpis.revenueDeltaAbs >= 0 ? "+" : ""}${formatCurrency(kpis.revenueDeltaAbs, currency)}`}
          footerNote="vs stesso periodo anno precedente"
          trend={trend}
          darkMode={darkMode}
          tooltip="Somma degli importi di tutte le fatture emesse nell'anno, indipendentemente dal pagamento."
        />

        <KpiCard
          title="Incassi"
          value={formatCurrency(kpis.incassiAnno, currency)}
          footerLeft={`${incassiDeltaPct >= 0 ? "+" : ""}${incassiDeltaPct.toFixed(2)}%`}
          footerRight={`${kpis.incassiDeltaAbs >= 0 ? "+" : ""}${formatCurrency(kpis.incassiDeltaAbs, currency)}`}
          footerNote="vs stesso periodo anno precedente"
          trend={incassiTrend}
          darkMode={darkMode}
          tooltip="Totale degli importi effettivamente incassati nell'anno. Nel forfettario, le tasse si calcolano sugli incassi."
        />

        <KpiCard
          title="Netto disponibile"
          value={formatCurrency(kpis.netAnnual, currency)}
          secondaryValue={formatCurrency(kpis.netMonthly, currency)}
          secondaryLabel={`Media ${kpis.mesiTrascorsi} mesi`}
          variant="success"
          darkMode={darkMode}
          tooltip="Incassi meno le tasse stimate da accantonare. È quanto puoi considerare 'tuo'."
        />

        <KpiCard
          title="Tasse da accantonare"
          value={formatCurrency(tasseValue, currency)}
          secondaryValue={formatCurrency(tasseMonthly, currency)}
          secondaryLabel={`Media ${kpis.mesiTrascorsi} mesi`}
          variant={haVersamenti && tasseValue <= 0 ? "success" : "danger"}
          darkMode={darkMode}
          to="/fiscal"
          tooltip={tasseTooltip}
          breakdown={
            haVersamenti
              ? [
                  { label: "Dovuto", value: formatCurrency(kpis.setAsideAnnual, currency) },
                  { label: "Versato", value: `− ${formatCurrency(versatoTasse, currency)}` },
                ]
              : null
          }
        />
      </div>

      {/* Seconda riga: Proiezione e soglia */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Proiezione fine anno (basata su incassi) */}
        <div className={`rounded-2xl border p-4 shadow-sm ${
          darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-xs flex items-center gap-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Proiezione incassi fine anno
                <HelpTooltip text="Stima del fatturato a fine anno basata sul ritmo di incassi dell'ultimo mese attivo." darkMode={darkMode} />
              </div>
              {kpis.proiezioneIncassi !== null ? (
                <div className={`mt-1 text-xl font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                  {formatCurrency(kpis.proiezioneIncassi, currency)}
                </div>
              ) : (
                <div className={`mt-1 text-xl font-semibold ${darkMode ? 'text-neutral-600' : 'text-neutral-400'}`}>
                  Non disponibile
                </div>
              )}
            </div>
            <div className="text-right">
              {kpis.proiezioneIncassi !== null ? (
                <>
                  <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Basata su ultimo mese attivo</div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`}>
                    {kpis.ultimoMeseAttivo && formatCurrency(kpis.incassiUltimoMeseAttivo, currency)}/mese
                  </div>
                </>
              ) : (
                <div className={`text-xs ${darkMode ? 'text-neutral-600' : 'text-neutral-400'}`}>
                  Nessun dato
                </div>
              )}
            </div>
          </div>
          {kpis.proiezioneIncassi !== null ? (
            <p className={`mt-2 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
              Incassi attuali + (incassi ultimo mese × mesi restanti)
            </p>
          ) : (
            <p className={`mt-2 text-xs ${darkMode ? 'text-neutral-600' : 'text-neutral-400'}`}>
              Nessun incasso da fatture emesse nel {kpis.selectedYear} ancora registrato
            </p>
          )}
        </div>

        {/* Indicatore soglie 85k / 100k - basato su INCASSI, palette Sunset Calm */}
        {(() => {
          // Determina lo stato della barra in base alla proiezione
          const raggiungeraSoglia = kpis.raggiungeraSoglia || false;
          const hasProjection = kpis.proiezioneIncassi !== null;

          // Colori progressivi: forfy (calmo) -> ambra (avviso) -> rosso (pericolo/uscita)
          const barColor = isOverExitThreshold
            ? "bg-red-600"
            : isOverThreshold || isDangerThreshold
              ? "bg-red-500"
              : isNearThreshold
                ? "bg-amber-500"
                : "bg-forfy-400";

          const getBgColor = () => {
            if (isOverExitThreshold) return darkMode ? "bg-red-900/40 border-red-700" : "bg-red-50 border-red-300";
            if (isOverThreshold) return darkMode ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200";
            if (isDangerThreshold) return darkMode ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200";
            if (isNearThreshold) return darkMode ? "bg-amber-900/30 border-amber-700" : "bg-amber-50 border-amber-200";
            if (hasProjection && !raggiungeraSoglia) return darkMode ? "bg-forfy-900/20 border-forfy-700" : "bg-forfy-50 border-forfy-200";
            return darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200";
          };

          const getTextColor = () => {
            if (isOverThreshold || isDangerThreshold) return darkMode ? "text-red-400" : "text-red-600";
            if (isNearThreshold) return darkMode ? "text-amber-400" : "text-amber-600";
            if (hasProjection && !raggiungeraSoglia) return darkMode ? "text-forfy-300" : "text-forfy-700";
            return darkMode ? "text-neutral-500" : "text-neutral-500";
          };

          const getValueColor = () => {
            if (isOverThreshold || isDangerThreshold) return darkMode ? "text-red-300" : "text-red-700";
            if (isNearThreshold) return darkMode ? "text-amber-300" : "text-amber-700";
            if (hasProjection && !raggiungeraSoglia) return darkMode ? "text-forfy-200" : "text-forfy-800";
            return darkMode ? "text-white" : "text-neutral-900";
          };

          return (
            <div className={`rounded-2xl border p-4 shadow-sm ${getBgColor()}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-xs flex items-center gap-1 ${getTextColor()}`}>
                    Soglia forfettario (su incassi)
                    <HelpTooltip text="Oltre €85.000 di incassi esci dal regime forfettario dall'anno successivo. Oltre €100.000 l'uscita è immediata, con applicazione dell'IVA." darkMode={darkMode} />
                  </div>
                  <div className={`mt-1 text-xl font-semibold ${getValueColor()}`}>
                    {Math.round(percentualeSoglia)}%
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs ${getTextColor()}`}>
                    {isOverThreshold ? "Superata di" : "Margine disponibile"}
                  </div>
                  <div className={`text-sm font-semibold ${isOverThreshold ? (darkMode ? "text-red-400" : "text-red-700") : (darkMode ? "text-forfy-300" : "text-forfy-700")}`}>
                    {isOverThreshold
                      ? formatCurrency(kpis.incassiAnno - soglia, currency)
                      : formatCurrency(kpis.margineSoglia, currency)
                    }
                  </div>
                </div>
              </div>
              {/* Barra progresso su scala 0-100k, con marcatore a 85k */}
              <div className="mt-3">
                <div className={`relative h-2.5 rounded-full overflow-hidden ${darkMode ? 'bg-neutral-700' : 'bg-neutral-200'}`}>
                  <div
                    className={`h-full rounded-full transition-all ${barColor}`}
                    style={{ width: `${percentualeSogliaEstesa}%` }}
                  />
                  {/* Marcatore soglia 85k */}
                  <div
                    className={`absolute top-0 bottom-0 w-0.5 ${darkMode ? 'bg-neutral-400' : 'bg-neutral-500'}`}
                    style={{ left: `${marcatore85kPct}%` }}
                    title={`Soglia ${formatCurrency(soglia, currency)}`}
                  />
                </div>
                <div className={`mt-1 flex justify-between text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  <span>{formatCurrency(kpis.incassiAnno, currency)}</span>
                  <span className="flex gap-2">
                    <span>{kpis.isRagguagliata ? formatCurrency(soglia, currency) : "85k"}</span>
                    <span>{kpis.isRagguagliata ? formatCurrency(sogliaExit, currency) : "100k"}</span>
                  </span>
                </div>
              </div>
              {/* Stima raggiungimento soglia */}
              {isOverExitThreshold ? (
                <p className={`mt-2 text-xs font-medium ${darkMode ? "text-red-400" : "text-red-700"}`}>
                  Superata la soglia di uscita immediata (100.000 €): dal prossimo incasso si esce dal forfettario con IVA.
                </p>
              ) : !isOverThreshold ? (
                <p className={`mt-2 text-xs ${getTextColor()}`}>
                  {!hasProjection ? (
                    <>Stima non disponibile — nessun incasso da fatture {kpis.selectedYear}</>
                  ) : raggiungeraSoglia && kpis.meseRaggiungimentoSoglia ? (
                    <>Stima raggiungimento: <strong>{kpis.meseRaggiungimentoSoglia}</strong></>
                  ) : raggiungeraSoglia && !kpis.meseRaggiungimentoSoglia ? (
                    <>Superamento previsto nell'anno</>
                  ) : (
                    <>Non previsto il raggiungimento quest'anno</>
                  )}
                </p>
              ) : (
                <p className={`mt-2 text-xs ${darkMode ? "text-red-400" : "text-red-700"}`}>
                  Dall'anno prossimo potresti uscire dal regime forfettario.
                </p>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
